import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import { FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { loggerSetFormDetails } from '../utils/logger';
import { PetClaimsData } from '../types/services';
import { TopConfig } from '../context/ConfigContext';
import { ClaimFormPayload } from '../types/claim-form-payload';
import { saveFormDataSave } from '../services/saveFormData';
import { shouldDebounceSave } from '../utils/getShouldDebounceSave';

export type AppStateValues = {
    isLoading: boolean;
    formId: string | undefined;
    formValues: FormValues;
    formConfig: FormConfig | undefined;
    formStartTime: number | undefined;
    resumeFromSave: {
        isResumed: boolean;
        firstSavedDate: number;
    };
    petClaimsData: PetClaimsData[] | undefined;
    topConfig: TopConfig | undefined;
    claimFormPayload: ClaimFormPayload | undefined;
    hasReachedSummary: boolean;
    claimNumber: string;
    error: string;
};

type AppStateActions = {
    setIsLoading: (isLoading: boolean) => void;
    setFormId: (formId: string | undefined) => void;
    setFormValue: (key: string, value: any) => void;
    setFormValueWithNoSaveInStorage: (key: string, value: any) => void;
    setFormConfig: (formConfig: FormConfig | undefined) => void;
    resetForm: () => void;
    resetFormConfig: () => void;
    resetFormValues: () => void;
    setFormStartTime: (time: number | undefined) => void;
    setAllFormValues: (formValues: AppStateValues['formValues']) => void;
    setResumedFromSave: (resumed: AppStateValues['resumeFromSave']) => void;
    resetResumedFromSave: () => void;
    setPetClaimsData: (petClaimsData: PetClaimsData[] | undefined) => void;
    setTopConfig: (topConfig: TopConfig | undefined) => void;
    setClaimFormPayload: (payload: ClaimFormPayload | undefined) => void;
    setHasReachedSummary: (hasReachedSummary: boolean) => void;
    setClaimNumber: (claimNumber: string) => void;
    setError: (error: string) => void;
};

const appStateValuesInit: AppStateValues = {
    formId: undefined,
    isLoading: false,
    formValues: {},
    formConfig: undefined,
    formStartTime: Date.now(),
    resumeFromSave: {
        isResumed: false,
        firstSavedDate: 0
    },
    petClaimsData: undefined,
    topConfig: undefined,
    claimFormPayload: undefined,
    hasReachedSummary: false,
    error: '',
    claimNumber: ''
};

let debounceTimer: NodeJS.Timeout | null = null;

const triggerSave = async (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    topConfig: TopConfig | undefined,
    debounce: boolean
) => {
    if (!formConfig?.enableSave || !topConfig) return;

    if (debounce) {
        if (debounceTimer) clearTimeout(debounceTimer);

        debounceTimer = setTimeout(async () => {
            await saveFormDataSave(formConfig, formValues, topConfig);
        }, 1000);
    } else {
        await saveFormDataSave(formConfig, formValues, topConfig);
    }
};

const useAppState = create<AppStateValues & AppStateActions>()(
    persist(
        (set) => ({
            ...appStateValuesInit,
            setFormId: (formId: string | undefined) => {
                set({ formId });
            },

            setIsLoading: (isLoading: boolean) => {
                set({ isLoading });
            },
            // Set form value and save to storage (if enableSave:true in formConfig)
            setFormValue: (key: string, value: any) => {
                set((state) => {
                    const updatedValues = { ...state.formValues, [key]: value };
                    const shouldDebounce = shouldDebounceSave(state.formConfig, key);

                    if (state.formConfig?.enableSave) {
                        triggerSave(state.formConfig, updatedValues, state.topConfig, shouldDebounce);
                    }

                    return { formValues: updatedValues };
                });
            },
            // Set form value without saving to storage regardless of formConfig.enableSave
            setFormValueWithNoSaveInStorage: (key: string, value: any) => {
                set((state) => {
                    const updatedValues = { ...state.formValues, [key]: value };
                    return { formValues: updatedValues };
                });
            },
            setFormConfig: (formConfig: FormConfig | undefined) => {
                set((state) => ({ formConfig }));
                if (formConfig) {
                    const formTitle = formConfig.caption;
                    const formPath = formConfig.formId;

                    loggerSetFormDetails(formTitle, formPath);
                }
            },
            // Preserves formConfig as the user is still
            // on the *form* after submitting/resetting
            // His entered *values*, however, will be reset
            resetForm: () =>
                set((state) => ({
                    ...appStateValuesInit,
                    formConfig: state.formConfig
                })),
            resetFormConfig: () =>
                set((state) => ({
                    petClaimsData: undefined,
                    formConfig: undefined
                })),
            resetFormValues: () =>
                set((state) => ({
                    petClaimsData: undefined,
                    formValues: []
                })),
            setFormStartTime: (time: number | undefined) => set(() => ({ formStartTime: time })),
            setAllFormValues: (formValues: AppStateValues['formValues']) => set((state) => ({ formValues })),
            setResumedFromSave: (resumed: AppStateValues['resumeFromSave']) =>
                set((state) => ({ resumeFromSave: resumed })),
            resetResumedFromSave: () => set((state) => ({ resumeFromSave: appStateValuesInit.resumeFromSave })),
            setPetClaimsData: (petClaimsData: PetClaimsData[] | undefined) => {
                if (petClaimsData) {
                    set({ petClaimsData });
                }
            },
            setTopConfig: (topConfig: TopConfig | undefined) => {
                if (topConfig) {
                    set({ topConfig });
                }
            },
            setClaimFormPayload: (claimFormPayload: ClaimFormPayload | undefined) => {
                set({ claimFormPayload });
            },
            setHasReachedSummary: (hasReachedSummary: boolean) => {
                set({ hasReachedSummary });
            },
            setClaimNumber: (claimNumber: string) => {
                set({ claimNumber });
            },
            setError: (error: string) => {
                set({ error });
            }
        }),
        {
            name: 'claim-form-v2-storage',
            storage: createJSONStorage(() => sessionStorage)
        }
    )
);

if (process.env.REACT_APP_MOUNT_APP_STATE_DEV_TOOLS === '1') {
    mountStoreDevtool('useAppState', useAppState);
}

export default useAppState;
